import { useState } from "react";
import { logger } from "../lib/logger";
import { toast } from "sonner";
import { Loader2, Lock, Settings } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AppShell } from "../components/AppShell";
import { api, formatError } from "../lib/api";

export default function SettingsPage() {
  const { user } = useAuth();
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password changed!");
      setCurPw("");
      setNewPw("");
    } catch (err) {
      logger.error("Password change error", err);
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-8" data-testid="settings-page">
        <h1 className="text-3xl font-black tracking-tighter text-zinc-950 mb-8 font-heading">Settings</h1>
        <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="profile-section">
          <h2 className="text-lg font-bold text-zinc-900 mb-4 font-heading flex items-center gap-2">
            <Settings className="w-5 h-5 text-brand" /> Profile
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-zinc-100">
              <span className="text-sm text-zinc-500">Name</span>
              <span className="text-sm font-medium text-zinc-900">{user?.name}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-zinc-100">
              <span className="text-sm text-zinc-500">Email</span>
              <span className="text-sm font-medium text-zinc-900">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-zinc-100">
              <span className="text-sm text-zinc-500">Role</span>
              <span className={`text-xs font-bold px-2 py-0.5 ${user?.role === "admin" ? "bg-brand/10 text-brand" : "bg-zinc-100 text-zinc-600"}`}>
                {user?.role === "admin" ? "Admin (Unlimited)" : "User"}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-zinc-500">Credits</span>
              <span className="text-sm font-bold text-zinc-900">
                {user?.credits}
                {user?.role === "admin" && <span className="text-xs text-brand ml-1">(never deducted)</span>}
              </span>
            </div>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 p-6" data-testid="password-section">
          <h2 className="text-lg font-bold text-zinc-900 mb-4 font-heading flex items-center gap-2">
            <Lock className="w-5 h-5 text-brand" /> Change Password
          </h2>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1">Current Password</label>
              <input data-testid="current-password-input" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} required className="w-full bg-zinc-50 border border-zinc-200 px-4 py-3 text-zinc-900 focus:border-brand focus:ring-1 focus:ring-brand outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1">New Password</label>
              <input data-testid="new-password-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={6} className="w-full bg-zinc-50 border border-zinc-200 px-4 py-3 text-zinc-900 focus:border-brand focus:ring-1 focus:ring-brand outline-none" placeholder="Min 6 characters" />
            </div>
            <button data-testid="change-password-btn" type="submit" disabled={loading} className="bg-brand text-white font-semibold px-6 py-2.5 hover:bg-brand-hover flex items-center gap-2 disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Change Password
            </button>
          </form>
        </div>
      </div>
    </AppShell>
  );
}
